import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { AdminService } from '../admin/admin.service';
import { SnackbarService } from 'src/app/core/snackbar.service';
import { IGroupUserList, GROUP_ROLE_ENUM, SYSTEM_ROLE_ENUM, GROUP_ROLE_BY_MANAGER_ENUM } from '@models/general/user';
import { IUserRole } from '@models/general/user-role';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrors_ENUM } from '@models/mood/helpers/errors';
import { IGroup } from '@models/general/group';
import { AuthService } from 'src/app/core/auth/auth.service';
import { Router } from '@angular/router';

interface RoleForm {
  userid: FormControl<string | null>;
  groupid: FormControl<string | null>;
  role: FormControl<string | null>;
}

@Component({
  selector: 'restoreid-group-user-list',
  templateUrl: './group-user-list.component.html',
  styleUrls: ['./group-user-list.component.scss']
})
export class GroupUserListComponent implements OnInit {
  private _groupId!: string;

  @Input() set groupId(value: string) {
    // check value format
    if (!value || !this.isUUID(value)) {
      this._NavigateTo('not-found');
    }
    this._groupId = value;
    this._updateExistingValues();
  };

  public get groupId() {
    return this._groupId;
  }

  isUUID(value: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  }

  roleForm: FormGroup<RoleForm>;

  public currentGroup: IGroup | null = null;
  public currentUserId!: string;
  public userGroupRole: string = '';
  public potentialGroupRoles: (GROUP_ROLE_BY_MANAGER_ENUM | GROUP_ROLE_ENUM)[] = [...Object.values(GROUP_ROLE_BY_MANAGER_ENUM)];
  public potentialGroupUsers : IGroupUserList[] = [];
  public groupUsersList : IGroupUserList[] = [];

  constructor(
    private _snackbarService: SnackbarService,
    private _changeRef: ChangeDetectorRef,
    private _adminService: AdminService,
    private _authService: AuthService,
    private _router: Router,
  )
  {
    this.currentUserId = this._authService.loggedInUserId!;

    this.roleForm = new FormGroup<RoleForm>({
      userid: new FormControl<string | null>(null, [Validators.required]),
      role: new FormControl<string | null>(null, [Validators.required]),
      groupid: new FormControl<string | null>(null)
    });
  }

  get saveButtonDisabled() {
    return this.roleForm.invalid || this.roleForm.pristine;
  }

  async ngOnInit() {
    await this.getUserGroupRole();
  }

  private async _updateExistingValues() {
    this.roleForm.reset();

    await this.getCurrentGroup();
    await this.getPotentialGroupUsers();
    await this.getGroupUsers();

    this.roleForm.markAsPristine();
  }

  async getUserGroupRole(){
    if(this._authService.loggedInUser?.isadmin){
      this.potentialGroupRoles.unshift(GROUP_ROLE_ENUM.MANAGER);
    }
    else
    {
      this.userGroupRole = await this._adminService.getRoleByGroupId(this.currentUserId, this.groupId);
      if(this.userGroupRole !== SYSTEM_ROLE_ENUM.MANAGER){
        this._NavigateTo('access-denied');
      }
    }
  }

  async getCurrentGroup(){
    const group = await this._adminService.findGroupById(this.groupId);
    if (group) {
      this.currentGroup = group;
    }
    else {
      this._NavigateTo('not-found');
    }
  }

  private _NavigateTo(path: string){
    this._router.navigate(['/', path]);
  }

  async getPotentialGroupUsers(){
    this.potentialGroupUsers = await this._adminService.getPotentialGroupUsers(this.groupId);
  }

  async getGroupUsers(){
    this.groupUsersList = await this._adminService.getGroupUsers(this.groupId);
  }

  async saveForm() {
    if (this.roleForm.valid) {
      const rawValue = this.roleForm.getRawValue();
      const roleInfo: IUserRole = {
        userid: rawValue.userid!,
        groupid: this.groupId,
        role: rawValue.role as GROUP_ROLE_ENUM
      };

      if (roleInfo.role === GROUP_ROLE_ENUM.MANAGER) {
        await this._handleManagerRole(roleInfo);
      }
      else {
        await this._addUserToGroup(roleInfo);
      }
    }
  }

  private async _handleManagerRole(roleInfo: IUserRole) {
    const isManager = await this._adminService.userRoleChecker(this.groupId, GROUP_ROLE_ENUM.MANAGER);
    if (isManager) {
        const confirmation = confirm("There is already a manager in this group. Do you want to overwrite?");
        if (confirmation) {
          await this._adminService.removeUserGroupByRole(GROUP_ROLE_ENUM.MANAGER, this.groupId);
          await this._addUserToGroup(roleInfo);
        }
    }
    else {
      await this._addUserToGroup(roleInfo);
    }
  }

  private async _addUserToGroup(roleInfo: IUserRole) {
    try {
      await this._adminService.addUserGroup(roleInfo);
      await this._updateExistingValues();
      this.roleForm.markAsPristine();
      this._snackbarService.openSuccessMessage("User added to group");
    }
    catch (error) {
      if (error instanceof HttpErrorResponse && error.error) {
        this.setErrorMessage(error);
      }
    }
  }

  async removeUser(user: IGroupUserList){
    const confirmation = confirm(`${this.userGroupRole === SYSTEM_ROLE_ENUM.MANAGER ? 'This user is manager of this group.' : ''} Are you sure you want to remove this user?`);
    if (!confirmation) {
      return;
    }
    await this._adminService.removeUserGroup(user.id, this.groupId);
    await this.getGroupUsers();
    await this.getPotentialGroupUsers();
  }

  public getErrorMessage(control: AbstractControl | null) {
    if (control!.getError("error")) {
      return control!.getError("error");
    }
  }

  public setErrorMessage(error: HttpErrorResponse){
    let errorObject = error.error as { message: string, code: ApiErrors_ENUM };
    if(errorObject.code === ApiErrors_ENUM.UserAlreadyAddedToAnotherGroup){
      this.useridControl?.setErrors({ error: errorObject.message });
    }

    this.roleForm.markAllAsTouched();
  }

  public get useridControl() {
    return this.roleForm.get('userid');
  }
}
